import { isHoliday } from '@holiday-jp/holiday_jp'
import { Tooltip } from 'antd'
import {
  startOfDay,
  addMonths,
  startOfMonth,
  endOfMonth,
  format,
  getDate
} from 'date-fns'
import { useMemo, type FC } from 'react'

import { Calendar } from '@/components/elements'
import { DESCRIPTIONS } from '@/constants/common/message.constants'
import { FORMAT } from '@/constants/common/message.constants'

import {
  CALENDAR_DATE_STATUS,
  CALENDAR_DATE_STYLES,
  type CalendarDateStatus
} from './constants'
import styles from './RegularScheduleCalendar.module.scss'

type Props = {
  baseDate: Date
  selectedDates: Date[]
  existingDates?: Date[]
  editingDate?: Date | null
  tourDates?: Date[]
  onDateSelect: (date: Date) => void
  onDateDeselect: (date: Date) => void
}

export const RegularScheduleCalendar: FC<Props> = ({
  baseDate,
  selectedDates,
  existingDates = [],
  editingDate = null,
  tourDates = [],
  onDateSelect,
  onDateDeselect
}) => {
  const months = useMemo(() => {
    const base = startOfDay(baseDate)
    return [
      {
        start: startOfMonth(base),
        end: endOfMonth(base),
        month: base
      },
      {
        start: startOfMonth(addMonths(base, 1)),
        end: endOfMonth(addMonths(base, 1)),
        month: addMonths(base, 1)
      },
      {
        start: startOfMonth(addMonths(base, 2)),
        end: endOfMonth(addMonths(base, 2)),
        month: addMonths(base, 2)
      }
    ]
  }, [baseDate])

  const isSameDay = (date1: Date, date2: Date) => {
    return startOfDay(date1).getTime() === startOfDay(date2).getTime()
  }

  const getDateStatus = (date: Date): CalendarDateStatus => {
    // 編集中の日付チェック(最優先)
    if (editingDate && isSameDay(date, editingDate)) {
      return CALENDAR_DATE_STATUS.EDITING
    }

    // 新規選択された日付チェック（既存以外で選択中）
    const isSelected = selectedDates.some((d) => isSameDay(d, date))
    const isExisting = existingDates.some((d) => isSameDay(d, date))

    // 新規選択（既存ではない）→ 青色
    if (isSelected && !isExisting) {
      return CALENDAR_DATE_STATUS.SELECTED
    }

    // 既存の定期運行 → 緑色
    if (isExisting) {
      return CALENDAR_DATE_STATUS.EXISTING
    }

    return CALENDAR_DATE_STATUS.NONE
  }

  // 実績化判定
  const hasTour = (date: Date): boolean => {
    return tourDates.some((d) => isSameDay(d, date))
  }

  const handleDateClick = (date: Date) => {
    const status = getDateStatus(date)

    // 編集中の日付は変更不可
    if (status === CALENDAR_DATE_STATUS.EDITING) {
      return
    }

    // 実績化されている日付は解除不可
    if (hasTour(date)) {
      return
    }

    // 発送日より前の日付は選択不可
    const isBeforeEditingDate =
      editingDate && startOfDay(date) < startOfDay(editingDate)
    if (isBeforeEditingDate) {
      return
    }

    if (
      status === CALENDAR_DATE_STATUS.SELECTED ||
      status === CALENDAR_DATE_STATUS.EXISTING
    ) {
      onDateDeselect(date)
    } else {
      onDateSelect(date)
    }
  }

  // スタイルをオブジェクトで定義
  const dateCellRender = (current: Date) => {
    const status = getDateStatus(current)
    const holiday = isHoliday(current)
    const isTour = hasTour(current)
    const isBeforeEditingDate =
      editingDate && startOfDay(current) < startOfDay(editingDate)

    const hasExistingRegularSchedule = status === CALENDAR_DATE_STATUS.EXISTING

    const tooltipTitle = (() => {
      if (status === CALENDAR_DATE_STATUS.EDITING) {
        return DESCRIPTIONS.CANNOT_DESELECT_EDITING_SHIPMENT_ORDER
      }
      if (isTour) {
        return DESCRIPTIONS.CANNOT_DESELECT_HAS_TOUR_ORDER
      }
      if (isBeforeEditingDate && hasExistingRegularSchedule) {
        return DESCRIPTIONS.CANNOT_DESELECT_TOUR_BEFORE_SHIPMENT_DATE
      }
      return undefined
    })()

    const getCellStyle = (): React.CSSProperties => {
      const baseStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        width: '100%',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: 'bold'
      }

      // 背景色と文字色の設定（定数を使用）
      if (status && status !== CALENDAR_DATE_STATUS.NONE) {
        const statusStyle = CALENDAR_DATE_STYLES[status]
        baseStyle.backgroundColor = statusStyle.backgroundColor
        baseStyle.color = statusStyle.color
        baseStyle.cursor = statusStyle.cursor
      } else if (holiday) {
        const holidayStyle = CALENDAR_DATE_STYLES.HOLIDAY
        baseStyle.color = holidayStyle.color
        baseStyle.backgroundColor = holidayStyle.backgroundColor
        baseStyle.cursor = holidayStyle.cursor
      } else {
        // 通常日
        const defaultStyle = CALENDAR_DATE_STYLES.DEFAULT
        baseStyle.backgroundColor = defaultStyle.backgroundColor
        baseStyle.color = defaultStyle.color
        baseStyle.cursor = defaultStyle.cursor
      }

      // ホバー効果（状態がない通常日のみ）
      if (!status && !holiday) {
        baseStyle.backgroundColor = 'transparent'
      }

      // 発送日前は無効風に見せる
      if (isBeforeEditingDate || isTour) {
        baseStyle.cursor = 'not-allowed'
        baseStyle.opacity = 0.6
      }

      return baseStyle
    }

    const cellContent = (
      <div
        className="relative h-full w-full"
        onClick={(e) => {
          e.stopPropagation()
          handleDateClick(current)
        }}
      >
        <div
          style={getCellStyle()}
          className={`
          ${!status && !holiday ? 'hover:bg-gray-50' : ''}
          ${isTour || isBeforeEditingDate ? 'cursor-not-allowed opacity-60' : ''}
        `}
        >
          {getDate(current)}
        </div>
      </div>
    )

    return <Tooltip title={tooltipTitle}>{cellContent}</Tooltip>
  }

  return (
    <div className="grid grid-cols-3 gap-4 overflow-x-auto py-2">
      {months.map((monthInfo, index) => (
        <div key={index} className="w-full flex-shrink-0">
          <Calendar
            className={styles.calendar}
            fullscreen={false}
            value={monthInfo.month}
            disabledDate={(date) => {
              return date < monthInfo.start || date > monthInfo.end
            }}
            fullCellRender={(current) => {
              if (current < monthInfo.start || current > monthInfo.end) {
                return <div className="h-full" />
              }
              return dateCellRender(current)
            }}
            headerRender={({ value }) => (
              <div className="px-2 py-1 text-center font-bold">
                {format(value, FORMAT.YEAR_MONTH_JA)}
              </div>
            )}
          />
        </div>
      ))}
    </div>
  )
}
